import React, { useEffect, useState } from "react";
import { Play } from "lucide-react";

// hqdefault.jpg is guaranteed to exist for every YouTube video (480x360)
// maxresdefault.jpg only exists if the uploader provided a HD thumbnail
const YouTubePlayer = ({ videoId, title = "Video", thumbnail }) => {
  const [playing, setPlaying] = useState(false);
  const [thumb, setThumb] = useState(
    thumbnail || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
  );

  // After mount, try to upgrade to maxresdefault if it loads correctly
  useEffect(() => {
    if (thumbnail || !videoId) return;
    const test = new Image();
    test.onload = () => {
      // YouTube returns a 120x90 gray placeholder when maxres doesn't exist.
      // Real thumbnails are >= 320 wide.
      if (test.naturalWidth > 320) {
        setThumb(`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`);
      }
    };
    test.src = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
  }, [videoId, thumbnail]);

  if (!videoId) return null;

  return (
    <div className="relative w-full aspect-video bg-neutral-900 overflow-hidden rounded-xl group shadow-lg">
      {playing ? (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={title}
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <button
          onClick={() => setPlaying(true)}
          className="w-full h-full relative block"
          aria-label={`Play ${title}`}
        >
          <img
            src={thumb}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => {
              // Final fallback chain
              if (e.currentTarget.src.includes("maxresdefault")) {
                e.currentTarget.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
              } else if (e.currentTarget.src.includes("hqdefault")) {
                e.currentTarget.src = `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
              }
            }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent group-hover:from-black/60 transition-colors duration-300" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 text-black flex items-center justify-center shadow-xl transition-transform duration-300 group-hover:scale-110">
              <Play className="w-7 h-7 md:w-8 md:h-8 ml-1" fill="currentColor" />
            </span>
          </div>
          {title && (
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 text-left">
              <span className="text-white text-sm md:text-base font-medium tracking-tight drop-shadow-lg">
                {title}
              </span>
            </div>
          )}
        </button>
      )}
    </div>
  );
};

export default YouTubePlayer;
